import {
  Button,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from "@heroui/react";
import { useState } from "react";

import { errorMessage } from "@/lib/api";
import { createPartner, type PartnerInput } from "./api";
import { PARTNER_FIELDS, PARTNER_ROLE_LABEL, type PartnerRole } from "./columns";

/**
 * 取引先の新規登録ダイアログ。
 *
 * 入力項目は一覧の列定義（[[PARTNER_FIELDS]]）から組み立てる。列を足したら
 * 登録フォームにも自動で出るようにして、二重管理を避ける。
 *
 * 取引先コードの重複（409）はダイアログを閉じずにその場で出す。入力し直せば
 * 解決する失敗のため。
 *
 * 用途（得意先 / 直送先）はテキストではないので列定義から組み立てず、
 * チェックボックスで別に出す。既定は得意先だけ（登録される取引先はたいてい
 * 得意先で、直送先は客先の一覧表から入れるため）。
 */
export default function PartnerCreateDialog({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (partnerName: string) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [roles, setRoles] = useState<Record<PartnerRole, boolean>>({
    is_client: true,
    is_ship_to: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setValues({});
    setRoles({ is_client: true, is_ship_to: false });
    setError(null);
    setSaving(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    const missing = PARTNER_FIELDS.filter((f) => f.required && !values[f.key]?.trim());
    if (missing.length > 0) {
      setError(`${missing.map((f) => f.label).join("・")}は必須です。`);
      return;
    }

    // どちらの用途でもない取引先は、どの選択肢にも出てこない。
    if (!roles.is_client && !roles.is_ship_to) {
      setError("得意先・直送先のどちらかには使う取引先にしてください。");
      return;
    }

    const input: PartnerInput = { ...roles };
    for (const field of PARTNER_FIELDS) {
      const value = values[field.key]?.trim();
      // 任意項目の空欄は送らない（API 側で未入力として扱われる）。
      if (value) (input as Record<string, string>)[field.key] = value;
    }

    setSaving(true);
    try {
      const created = await createPartner(input);
      onCreated(created.partner_name);
      close();
    } catch (e) {
      setError(errorMessage(e, "登録に失敗しました。"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && close()}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>取引先の新規登録</span>
          <span className="text-small font-normal text-default-500">
            取引先コードと取引先名は必須です。ほかの項目は後から一覧で編集できます。
          </span>
        </ModalHeader>

        <ModalBody>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {PARTNER_FIELDS.map((field) =>
              field.kind === "select" ? (
                <Select
                  key={field.key}
                  label={field.label}
                  aria-label={field.label}
                  labelPlacement="outside"
                  placeholder="（未設定）"
                  variant="bordered"
                  radius="sm"
                  selectedKeys={values[field.key] ? [values[field.key]] : []}
                  onSelectionChange={(keys) => {
                    const key = [...keys][0];
                    setValues((prev) => ({
                      ...prev,
                      [field.key]: key == null ? "" : String(key),
                    }));
                    setError(null);
                  }}
                >
                  {(field.options ?? []).map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>
              ) : (
                <Input
                  key={field.key}
                  label={field.label}
                  aria-label={field.label}
                  labelPlacement="outside"
                  placeholder=" "
                  variant="bordered"
                  radius="sm"
                  isRequired={field.required}
                  value={values[field.key] ?? ""}
                  onValueChange={(v) => {
                    setValues((prev) => ({ ...prev, [field.key]: v }));
                    setError(null);
                  }}
                  // 住所は長いので 2 列ぶん使う。
                  className={
                    field.key === "address" || field.key === "sub_address"
                      ? "sm:col-span-2"
                      : ""
                  }
                />
              ),
            )}
          </div>

          <div className="mt-2">
            <p className="mb-1 text-small text-default-600">用途</p>
            <div className="flex gap-4">
              {(Object.keys(PARTNER_ROLE_LABEL) as PartnerRole[]).map((role) => (
                <Checkbox
                  key={role}
                  size="sm"
                  isSelected={roles[role]}
                  onValueChange={(on) => {
                    setRoles((prev) => ({ ...prev, [role]: on }));
                    setError(null);
                  }}
                >
                  {PARTNER_ROLE_LABEL[role]}
                </Checkbox>
              ))}
            </div>
            <p className="mt-1 text-tiny text-default-400">
              得意先と直送先は兼ねられます。選択肢に出す用途をチェックしてください。
            </p>
          </div>

          {error && <p className="mt-2 text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" onPress={close}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="create-submit"
            isLoading={saving}
            onPress={() => void submit()}
          >
            登録
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
